import React, { createContext, FC, useCallback, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { lazy } from 'yup';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import Disclaimers from 'buyside/components/presentation/WhitePaperForm/Disclaimers/disclaimers';
import TitleAndCategories from 'buyside/components/presentation/WhitePaperForm/TitleAndCategories/titleAndCategories';
import WriteupAndAttachments from 'buyside/components/presentation/WhitePaperForm/WriteupAndAttachments/writeupAndAttachments';
import ResearchFormWizard, {
  ResearchWizardStep
} from 'buyside/components/presentation/ResearchFormWizard/researchFormWizard';

import {
  determineWhitePaperCompletedSteps,
  generateWhitePaperValidationSchema,
  generateWhitepaperDefaultValues,
  WhitePaperFormInput
} from 'buyside/util/researchSubmission';

import {
  WhitePaperSubmissionDraftFragment,
  WhitePaperSubmissionQuery,
  useCreateOrUpdateWhitePaperMutation,
  useWhitePaperSubmissionQuery
} from 'interfaces/graphql-typings';

export const WhitePaperFormContext = createContext<{
  currentUser?: WhitePaperSubmissionQuery['currentUser'];
  whitePaperId?: string;
  isDraft: boolean;
}>({ isDraft: true });

const steps = [
  {
    label: 'Title & Categories',
    name: 'title_and_categories',
    component: TitleAndCategories
  },
  {
    label: 'Writeup & Attachments',
    name: 'writeup_and_attachments',
    component: WriteupAndAttachments
  },
  {
    label: 'Review & Submit',
    name: 'review_and_submit',
    component: Disclaimers
  }
];

const WhitePaperForm: FC = () => {
  const navigate = useNavigate();

  const { id: whitePaperId } = useParams();

  const [currentStep, setCurrentStep] = useState<ResearchWizardStep>(steps[0]);
  const [completedSteps, setCompletedSteps] = useState<ResearchWizardStep[]>([]);

  const methods = useForm<WhitePaperFormInput>({
    defaultValues: generateWhitepaperDefaultValues(),
    resolver: yupResolver(
      lazy(() =>
        generateWhitePaperValidationSchema({
          currentStepName: currentStep.name
        })
      )
    )
  });

  const { reset } = methods;

  const handleSetCurrentStep = useCallback((step: ResearchWizardStep) => {
    window.scrollTo(0, 0);
    return setCurrentStep(step);
  }, []);

  const handleResetForm = useCallback(
    (whitePaper?: WhitePaperSubmissionDraftFragment, customStep?: ResearchWizardStep) => {
      const defaultValues = generateWhitepaperDefaultValues(whitePaper);
      reset(defaultValues);

      const completedStepNames = determineWhitePaperCompletedSteps(defaultValues);
      const { completedSteps, incompleteSteps } = steps.reduce(
        (
          memo: { completedSteps: ResearchWizardStep[]; incompleteSteps: ResearchWizardStep[] },
          step
        ) => {
          completedStepNames.includes(step.name)
            ? memo.completedSteps.push(step)
            : memo.incompleteSteps.push(step);
          return memo;
        },
        { completedSteps: [], incompleteSteps: [] }
      );
      setCompletedSteps(completedSteps);
      handleSetCurrentStep(customStep || incompleteSteps[0] || steps[steps.length - 1]);
    },
    [handleSetCurrentStep, reset]
  );

  const { data: whitePaperData, error, loading: whitePaperLoading } = useWhitePaperSubmissionQuery({
    onCompleted: ({ whitePaper }) => handleResetForm(whitePaper),
    skip: !whitePaperId,
    variables: { id: `${whitePaperId}` }
  });

  const [handleCreateOrUpdateWhitePaper] = useCreateOrUpdateWhitePaperMutation();

  const { currentUser, whitePaper } = whitePaperData || {};
  const { id: currentUserId, isAdmin } = currentUser || {};
  const { status, user: whitePaperUser } = whitePaper || {};
  const { id: whitePaperUserId } = whitePaperUser || {};

  const handleCreateOrUpdateDraft = useCallback(
    (values: WhitePaperFormInput, nextStep: ResearchWizardStep, isSubmission: boolean) => {
      const variables = {
        ...values,
        attachments: values.attachments.map(
          ({ id, attachmentType, file, filename, researchType }) => ({
            id,
            attachmentType,
            file,
            filename,
            researchType
          })
        ),
        currentStep: currentStep.name,
        isSubmission,
        mentionedEntities: values.mentionedEntities.map(({ value }) => value)
      };

      return handleCreateOrUpdateWhitePaper({ variables })
        .then(response => {
          const whitePaper = response.data?.createOrUpdateWhitePaper?.whitePaper;
          handleResetForm(whitePaper, nextStep);
          if (!whitePaperId && whitePaper?.id) navigate(whitePaper.id, { replace: true });
        })
        .catch(error => toast.error(error.message, { autoClose: 7500, theme: 'colored' }));
    },
    [currentStep.name, handleCreateOrUpdateWhitePaper, handleResetForm, navigate, whitePaperId]
  );

  if (whitePaperUserId && whitePaperUserId !== currentUserId && !isAdmin)
    return (
      <Navigate
        to="/"
        state={{
          cannotEditError: `You cannot edit Macro/Thematic Report with ID ${whitePaperId}. Contact support@sumzero.com if you believe this is an error.`
        }}
      />
    );

  if (error) throw error;

  const isDraft = status === 'draft' || !whitePaper;

  return (
    <WhitePaperFormContext.Provider
      value={{
        currentUser,
        whitePaperId,
        isDraft
      }}
    >
      <FormProvider {...methods}>
        <ResearchFormWizard
          completedSteps={completedSteps}
          currentStep={currentStep}
          handleCreateOrUpdateDraft={handleCreateOrUpdateDraft}
          handleSetCurrentStep={handleSetCurrentStep}
          isDraft={isDraft}
          isLoading={whitePaperLoading}
          researchPath={`/pro/research/white_papers/${whitePaperId}`}
          researchType="Macro/Thematic Report"
          steps={steps}
        />
      </FormProvider>
    </WhitePaperFormContext.Provider>
  );
};

export default WhitePaperForm;
